import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Flag, MapPin, Trash2, Camera, Users, AlertOctagon, Megaphone } from 'lucide-react';
import {
  Post, PostCategory, postsApi, DOWNVOTE_REASONS, DownvoteReason,
  REPORT_REASONS, ReportReason,
} from '../services/postsApi';

const CATEGORY_LABELS: Record<PostCategory, string> = {
  police_activity: 'POLICE',
  shooting:        'SHOOTING/GUNFIRE',
  fire_ems:        'FIRE/EMS',
  traffic:         'TRAFFIC',
  pursuit:         'PURSUIT',
  containment:     'CONTAINMENT',
  suspicious:      'SUSPICIOUS',
  community:       'COMMUNITY',
  other:           'OTHER',
};
const CATEGORY_COLORS: Record<PostCategory, string> = {
  shooting:        'bg-red-600/30 text-red-200 border-red-500/40',
  pursuit:         'bg-red-600/30 text-red-200 border-red-500/40',
  containment:     'bg-red-600/30 text-red-200 border-red-500/40',
  police_activity: 'bg-orange-600/30 text-orange-200 border-orange-500/40',
  suspicious:      'bg-orange-600/30 text-orange-200 border-orange-500/40',
  fire_ems:        'bg-amber-600/30 text-amber-200 border-amber-500/40',
  traffic:         'bg-amber-600/30 text-amber-200 border-amber-500/40',
  community:       'bg-emerald-600/30 text-emerald-200 border-emerald-500/40',
  other:           'bg-slate-600/30 text-slate-200 border-slate-500/40',
};

function timeAgo(ts: number) {
  const d = Math.floor(Date.now() / 1000 - ts);
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

interface Props {
  post: Post;
  isSignedIn: boolean;
  isAdmin?: boolean;
  selfUid?: string | null;
  onPatch: (id: number, patch: Partial<Post>) => void;
  onRemove: (id: number) => void;
  onLocate?: (lat: number, lng: number) => void;
  onSignInRequired?: () => void;
}

const PostCard: React.FC<Props> = ({
  post, isSignedIn, isAdmin, selfUid, onPatch, onRemove, onLocate, onSignInRequired,
}) => {
  const [showDownvoteMenu, setShowDownvoteMenu] = useState(false);
  const [showReportMenu, setShowReportMenu]     = useState(false);
  const [busy, setBusy] = useState(false);
  const isOwner = !!selfUid && selfUid === post.author?.uid;

  const requireSignIn = () => {
    if (!isSignedIn && onSignInRequired) onSignInRequired();
    return !isSignedIn;
  };

  const upvote = async () => {
    if (requireSignIn()) return;
    if (busy || isOwner) return;
    setBusy(true);
    try {
      const next = post.myVote === 1 ? null : 1;
      // Optimistic
      onPatch(post.id, {
        upvotes: post.upvotes + (next === 1 ? 1 : -1) + (post.myVote === -1 ? 0 : 0),
        downvotes: post.downvotes - (post.myVote === -1 ? 1 : 0),
        myVote: next as any,
      });
      if (next === 1) await postsApi.vote(post.id, 1);
      // Server rejects "no vote" — we don't have an unvote endpoint yet.
      // For now upvote re-press is a no-op visually if next === null.
    } catch (e) {
      onPatch(post.id, { myVote: post.myVote });
    } finally { setBusy(false); }
  };

  const downvote = async (reason: DownvoteReason) => {
    if (requireSignIn()) return;
    if (busy || isOwner) return;
    setBusy(true); setShowDownvoteMenu(false);
    try {
      onPatch(post.id, {
        downvotes: post.downvotes + (post.myVote === -1 ? 0 : 1),
        upvotes: post.upvotes - (post.myVote === 1 ? 1 : 0),
        myVote: -1,
      });
      await postsApi.vote(post.id, -1, reason);
    } catch {
      onPatch(post.id, { myVote: post.myVote });
    } finally { setBusy(false); }
  };

  const report = async (reason: ReportReason) => {
    if (requireSignIn()) return;
    setShowReportMenu(false);
    try {
      const r = await postsApi.report(post.id, reason);
      onPatch(post.id, { reportCount: r.reportCount });
    } catch (e: any) {
      if (e?.status === 409) {
        // already reported — silent
      } else {
        alert(e?.message || 'Report failed.');
      }
    }
  };

  const remove = async () => {
    if (!confirm('Delete this post?')) return;
    try {
      await postsApi.delete(post.id);
      onRemove(post.id);
    } catch (e: any) {
      alert(e?.message || 'Delete failed.');
    }
  };

  const promote = async () => {
    if (!confirm('Promote this post — pin to top of feed for 24 hours?')) return;
    try {
      const r = await postsApi.promote(post.id, 24);
      onPatch(post.id, { promotion: { id: r.id, endsAt: r.endsAt, active: true } });
    } catch (e: any) {
      alert(e?.message || 'Promote failed.');
    }
  };
  const unpromote = async () => {
    if (!confirm('Cancel promotion?')) return;
    try {
      await postsApi.unpromote(post.id);
      onPatch(post.id, { promotion: null });
    } catch (e: any) {
      alert(e?.message || 'Cancel failed.');
    }
  };

  const author = post.author;
  const initial = (author?.displayName || author?.username || '?').charAt(0).toUpperCase();
  const archived = post.status === 'archived';
  const promoted = !!post.promotion?.active;

  return (
    <article className={`relative rounded-lg border bg-slate-900/70 backdrop-blur-sm p-4 mb-3 transition ${promoted ? 'border-amber-500/70 ring-1 ring-amber-500/20' : archived ? 'border-slate-700/40 opacity-75' : 'border-amber-700/30 hover:border-amber-500/60'}`}>
      {promoted && (
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-600/30 text-amber-200 border border-amber-500/40 font-bold">
          Promoted
        </div>
      )}
      <header className="flex items-center gap-2 mb-2 text-xs">
        {author?.photoURL ? (
          <img src={author.photoURL} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-slate-200">{initial}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-slate-200 font-semibold truncate">
              {author?.displayName || author?.username || 'Anonymous'}
            </span>
            {author?.username && author?.displayName && author.username !== author.displayName && (
              <span className="text-slate-500 text-[10px] truncate">@{author.username}</span>
            )}
            {author?.trustedReporter && (
              <span className="text-cyan-300 text-[10px] uppercase tracking-wide font-bold border border-cyan-400/40 rounded px-1">Verified</span>
            )}
            {author?.role === 'trusted_source' && (
              <span className="text-amber-300 text-[10px] uppercase tracking-wide font-bold border border-amber-400/40 rounded px-1">Partner</span>
            )}
          </div>
          <div className="text-slate-500">
            {author?.neighborhood ? `${author.neighborhood} · ` : ''}{timeAgo(post.createdAt)}
            {archived && <span className="ml-1 text-slate-600">· archived</span>}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${CATEGORY_COLORS[post.category]}`}>
          {CATEGORY_LABELS[post.category]}
        </span>
      </header>

      {post.corroboration && (
        <div className="flex flex-wrap items-center gap-1 mb-2 text-[10px] uppercase tracking-wide font-bold">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-600/30 text-cyan-200 border border-cyan-500/40">
            <Users size={11} /> Corroborated
            {post.corroboration.reporterCount != null && ` · ${post.corroboration.reporterCount} reporters`}
          </span>
          {post.corroboration.notInCad && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-600/30 text-amber-200 border border-amber-500/40">
              <AlertOctagon size={11} /> Not in CAD
            </span>
          )}
        </div>
      )}

      <h3 className="text-slate-100 font-semibold leading-tight mb-1">{post.title}</h3>
      <p className="text-sm text-slate-300 whitespace-pre-wrap">{post.description}</p>

      {post.media && post.media.kind === 'image' && (
        <a href={post.media.url} target="_blank" rel="noreferrer" className="block mt-2">
          <img
            src={post.media.thumb || post.media.url}
            alt=""
            loading="lazy"
            className="rounded border border-slate-700/60 max-h-80 w-full object-cover"
          />
        </a>
      )}

      {post.attribution && (
        <div className="mt-2 text-[11px] text-slate-400">
          Cross-posted from{' '}
          <a href={post.attribution.url} target="_blank" rel="noreferrer"
            className="text-amber-300 hover:text-amber-200 underline underline-offset-2 break-all">
            {post.attribution.label || post.attribution.url}
          </a>
        </div>
      )}

      {post.location && (
        <button
          onClick={() => onLocate && onLocate(post.location!.lat, post.location!.lng)}
          className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200"
        >
          <MapPin size={12} /> Show on map
        </button>
      )}

      <footer className="flex items-center gap-1 mt-3 text-xs text-slate-400">
        <button
          onClick={upvote}
          disabled={busy || isOwner}
          className={`flex items-center gap-1 px-2 py-1 rounded ${post.myVote === 1 ? 'bg-emerald-600/30 text-emerald-200' : 'hover:bg-slate-800'} disabled:opacity-50`}
          title={isOwner ? 'Cannot vote on your own post' : 'Upvote'}
        >
          <ChevronUp size={14} /> {post.upvotes}
        </button>

        <div className="relative">
          <button
            onClick={() => { if (!requireSignIn() && !isOwner) setShowDownvoteMenu(s => !s); }}
            disabled={busy || isOwner}
            className={`flex items-center gap-1 px-2 py-1 rounded ${post.myVote === -1 ? 'bg-red-700/30 text-red-200' : 'hover:bg-slate-800'} disabled:opacity-50`}
            title={isOwner ? 'Cannot vote on your own post' : 'Downvote'}
          >
            <ChevronDown size={14} /> {post.downvotes}
          </button>
          {showDownvoteMenu && (
            <div className="absolute z-30 top-full mt-1 left-0 bg-slate-900 border border-slate-700 rounded shadow-lg py-1 min-w-[140px]">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">Reason</div>
              {DOWNVOTE_REASONS.map(r => (
                <button key={r} onClick={() => downvote(r)}
                  className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-800 capitalize">
                  {r.replace('_', ' ')}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative ml-auto">
          <button
            onClick={() => { if (!requireSignIn()) setShowReportMenu(s => !s); }}
            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
            title="Report"
          >
            <Flag size={12} />
          </button>
          {showReportMenu && (
            <div className="absolute z-30 top-full mt-1 right-0 bg-slate-900 border border-slate-700 rounded shadow-lg py-1 min-w-[140px]">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">Report reason</div>
              {REPORT_REASONS.map(r => (
                <button key={r} onClick={() => report(r)}
                  className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-800 capitalize">
                  {r.replace('_', ' ')}
                </button>
              ))}
            </div>
          )}
        </div>

        {(isOwner || isAdmin) && !post.promotion?.active && (
          <button onClick={promote} className="ml-1 px-2 py-1 rounded hover:bg-slate-800 text-amber-300" title="Pin to top for 24h">
            <Megaphone size={12} />
          </button>
        )}
        {(isOwner || isAdmin) && post.promotion?.active && (
          <button onClick={unpromote} className="ml-1 px-2 py-1 rounded hover:bg-slate-800 text-amber-200" title="Cancel pin">
            <Megaphone size={12} className="opacity-60"/>
          </button>
        )}
        {(isOwner || isAdmin) && (
          <button onClick={remove} className="ml-1 px-2 py-1 rounded hover:bg-slate-800 text-red-300" title="Delete">
            <Trash2 size={12} />
          </button>
        )}
      </footer>
    </article>
  );
};

export default PostCard;
